"use client";

import React, { useEffect, useState } from "react";
import EmblaCarousel from "../EmblaCarousel/EmblaCarousel";
import type { EmblaOptionsType } from "embla-carousel";
import Image from "next/image";
import Link from "next/link";
import { Check } from "lucide-react";
import "./produktSlider.css";

// -----------------------------
// Slide-Daten
// -----------------------------
const Slides = [
  {
    src: "/Bctx.webp",
    alt: "Kassensystem im Schuhgeschäft",
    headline: "CASHTEX Profi für Einzelhandel und Dienstleister",
    description:
      "Schneller kassieren, sauber verwalten und klare Abläufe – passend für deinen täglichen Betrieb.",
    bullets: [
      "Einfache Bedienung im Alltag",
      "Schnelle Abläufe an der Kasse",
      "Reibungslose Verwaltung von Artikeln & Kunden",
    ],
    cta: "Mehr erfahren",
    to: "/kassensysteme/schuhe",
  },
  {
    src: "/index/products/ModeProfiStartseite.jpg",
    alt: "Regal mit Kleidung",
    headline: "ModeProfi – Warenwirtschaft für die Textilbranche",
    description:
      "Bestände, Artikelvarianten und Prozesse im Griff – vom Einkauf bis zum Verkauf.",
    bullets: [
      "Artikel & Varianten übersichtlich",
      "Bestände und Nachbestellung im Blick",
      "Effiziente Abläufe für Teams",
    ],
    cta: "Mehr erfahren",
    to: "/modeprofi",
  },
  {
    src: "/index/products/Messe.jpg",
    alt: "Messestand mit Büchern und Menschen",
    headline: "Office4Trade – Mobile Auftragsabwicklung",
    description:
      "Aufträge direkt beim Kunden erfassen und sicher in die Prozesse integrieren.",
    bullets: [
      "Mobile Auftragserfassung",
      "Weniger Fehler durch klare Workflows",
      "Schneller Abschluss beim Kunden",
    ],
    cta: "Mehr erfahren",
    to: "/office4trade",
  },
];

// -----------------------------
// Embla Optionen
// -----------------------------
const options: EmblaOptionsType = {
  loop: true,
};

// -----------------------------
// Typen
// -----------------------------
type SlideType = {
  src: string;
  alt: string;
  headline: string;
  description: string;
  bullets: string[];
  cta: string;
  to: string;
};

// -----------------------------
// Slides JSX
// -----------------------------
const slides: React.ReactElement[] = Slides.map((slide: SlideType, idx: number) => (
  <div key={idx} className="w-full px-0 sm:px-2">
<div
  className="
    mx-auto w-full
    overflow-hidden
    rounded-3xl
    bg-white
    ring-1 ring-black/10
  "
      >
        <div className="flex flex-col lg:flex-row items-stretch">
          {/* LINKS: Text */}
          <div className="w-full lg:w-[46%] p-10 sm:p-14 flex flex-col justify-center">
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-gray-900 leading-tight">
              {slide.headline}
            </h3>

            <p className="mt-4 text-gray-600 text-base sm:text-lg leading-relaxed max-w-xl font-normal">
              {slide.description}
            </p>

            {/* Bullets – bewusst kleiner & ruhiger */}
            <ul className="mt-6 space-y-2 text-sm sm:text-base text-gray-700">
              {slide.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3 leading-relaxed">
                  <Check className="mt-0.5 h-3.5 w-3.5 text-[#194172] flex-none" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-2">
              <Link
                href={slide.to}
                className="
              inline-flex items-center justify-center
              rounded-full
              px-7 py-3
              text-sm font-semibold
              text-white
              bg-#031628
              transition-all duration-300 ease-out
              hover:-translate-y-1 hover:shadow-lg"
              >
                {slide.cta}
              </Link>
            </div>
          </div>

          {/* RECHTS: Bild */}
          <div className="w-full lg:w-[54%] relative h-[340px] sm:h-[440px] lg:h-[560px]">
            <Image
              src={slide.src}
              alt={slide.alt}
              fill
              priority={idx === 0}
              quality={90}
              sizes="(max-width: 1024px) 100vw, 54vw"
              className="object-cover"
            />
          </div>
        </div>
      </div>
    </div>
  ),
);
const userOptions = {
  playOnInit: true,
  delay: 10000,
  stopOnMouseEnter: true,
  stopOnInteraction: false,
};
// -----------------------------
// Component
// -----------------------------
export default function ProduktSliderClean() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return <div style={{ height: 300 }} />;
  }

  return (
    <EmblaCarousel
      className="products"
      options={options}
      userOptions={userOptions}
      slides={slides}
      arrows={true}
    />
  );
}
